import React from 'react';
import { motion } from 'framer-motion';
import {
  Eye,
  Edit,
  Copy,
  CheckCircle2,
  Trash2,
  MoreVertical,
  ArrowRight,
  Plane,
  Package,
  Weight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  COLUMN_ACTIONS,
  ICON_MAP,
  getPrimaryAction,
  isActionEnabled,
} from './columns/columnActions';

/**
 * Tarjeta de solicitud aérea para el tablero Kanban.
 *
 * Lee las acciones desde `columns/columnActions.js` para que
 * cada columna del kanban pueda personalizar su set sin tocar
 * este archivo.
 *
 * Props:
 *  - r: registro de la solicitud aérea
 *  - variant: 'cotizaciones' | 'en-curso' | 'finalizadas' | 'rechazadas'
 *  - onAction: (actionKey, r) => void
 *  - actionsMode: 'full' | 'view' (default 'full')
 *      - 'full' → renderiza la acción primaria + menú ⋮ con todas
 *        las acciones de la columna.
 *      - 'view' → oculta la acción primaria y el menú ⋮. Solo
 *        permite abrir el detalle haciendo click en la tarjeta.
 *        Útil en el Kanban (overview) cuando las acciones se
 *        gestionan en los tableros detallados.
 */

const VARIANT_BADGE = {
  cotizaciones: 'bg-amber-100 text-amber-800 border-amber-200',
  'en-curso': 'bg-blue-100 text-blue-800 border-blue-200',
  finalizadas: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rechazadas: 'bg-rose-100 text-rose-800 border-rose-200',
};

const VARIANT_ACCENT = {
  cotizaciones: 'before:bg-amber-400',
  'en-curso': 'before:bg-blue-500',
  finalizadas: 'before:bg-emerald-500',
  rechazadas: 'before:bg-rose-500',
};

const shortId = (id) => (id ? String(id).slice(-6) : '—');
const formatCodigo = (r) => r?.codigo || shortId(r?._id);
const formatCliente = (r) => r?.clienteSnapshot?.nombre || shortId(r?.clienteId);
const formatClienteNit = (r) => r?.clienteSnapshot?.nit || '';

const formatRuta = (r) => {
  const a = r?.aeropuertoCargaSnapshot;
  const b = r?.aeropuertoDescargaSnapshot;
  const leftCity = a?.ciudad || a?.nombre;
  const rightCity = b?.ciudad || b?.nombre;
  const leftIata = a?.iata;
  const rightIata = b?.iata;
  if (leftCity && rightCity) {
    return `${leftCity}${leftIata ? ` (${leftIata})` : ''} → ${rightCity}${rightIata ? ` (${rightIata})` : ''}`;
  }
  if (leftIata && rightIata) return `${leftIata} → ${rightIata}`;
  if (r?.paisOrigen && r?.paisDestino) return `${r.paisOrigen} → ${r.paisDestino}`;
  return `${shortId(r?.aeropuertoCargaId)} → ${shortId(r?.aeropuertoDescargaId)}`;
};

const formatRutaSub = (r) => {
  const a = r?.aeropuertoCargaSnapshot;
  const b = r?.aeropuertoDescargaSnapshot;
  const leftPais = a?.pais || r?.paisOrigen;
  const rightPais = b?.pais || r?.paisDestino;
  if (leftPais && rightPais) return `${leftPais} → ${rightPais}`;
  return '';
};

const OperationCard = ({ r, variant = 'cotizaciones', onAction, actionsMode = 'full' }) => {
  const codigo = formatCodigo(r);
  const estado = r?.estado || '—';
  const cliente = formatCliente(r);
  const clienteNit = formatClienteNit(r);
  const ruta = formatRuta(r);
  const rutaSub = formatRutaSub(r);
  const incoterm = r?.incotermSnapshot?.codigo || '';
  const tipo = r?.tipoOperacion || '';
  const numPiezas = r?.numPiezas;
  const pesoTotal = r?.pesoTotal;

  // Acciones leídas del archivo compartido. En modo 'view' (Kanban)
  // no mostramos ninguna acción: solo se puede abrir el detalle
  // haciendo click en la tarjeta.
  const showActions = actionsMode !== 'view';
  const actions = showActions ? COLUMN_ACTIONS[variant] || [] : [];
  const primary = showActions ? getPrimaryAction(variant) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      whileHover={{ y: -2 }}
      className={cn(
        'group relative rounded-lg border bg-card shadow-sm hover:shadow-md transition-all cursor-pointer',
        'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-lg',
        VARIANT_ACCENT[variant],
      )}
      onClick={() => onAction && onAction('view', r)}
    >
      <div className="p-3 pl-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <Plane className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="font-mono text-xs font-semibold text-foreground truncate">
                {codigo}
              </span>
            </div>
            <p
              className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate"
              title={r?._id}
            >
              {shortId(r?._id)}
            </p>
          </div>
          <span
            className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0',
              VARIANT_BADGE[variant],
            )}
          >
            {estado}
          </span>
        </div>

        <p className="text-sm font-semibold text-foreground truncate" title={cliente}>
          {cliente}
        </p>
        {clienteNit && (
          <p className="text-[10px] text-muted-foreground -mt-1.5 truncate">
            NIT {clienteNit}
          </p>
        )}

        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="truncate flex-1" title={ruta}>{ruta}</span>
            <ArrowRight className="h-3 w-3 shrink-0" />
            <span className="truncate flex-1 text-right invisible">·</span>
          </div>
          {rutaSub && (
            <p className="text-[10px] text-muted-foreground truncate" title={rutaSub}>
              {rutaSub}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1 border-t border-border/50 flex-wrap">
          {tipo && (
            <span className="capitalize truncate" title={tipo}>{tipo}</span>
          )}
          {incoterm && (
            <span className="font-mono text-[10px] px-1 py-0.5 bg-muted rounded">
              {incoterm}
            </span>
          )}
          {numPiezas != null && (
            <span className="flex items-center gap-0.5 shrink-0">
              <Package className="h-3 w-3" />
              {numPiezas}
            </span>
          )}
          {pesoTotal != null && (
            <span className="flex items-center gap-0.5 shrink-0">
              <Weight className="h-3 w-3" />
              {Number(pesoTotal).toFixed(2)} kg
            </span>
          )}
        </div>

        {showActions && (
          <div
            className="flex items-center justify-end gap-1 pt-1 -mb-1"
            onClick={(e) => e.stopPropagation()}
          >
            {primary && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] text-primary hover:bg-primary/10"
                onClick={() => onAction && onAction(primary.key, r)}
                disabled={!isActionEnabled(primary, r)}
              >
                {primary.label}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  aria-label="Más acciones"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {actions.map((a) => {
                  if (a.key === 'view') return null;
                  const Icon = ICON_MAP[a.icon] || Eye;
                  const disabled = !isActionEnabled(a, r);
                  return (
                    <DropdownMenuItem
                      key={a.key}
                      onClick={() => onAction && onAction(a.key, r)}
                      disabled={disabled}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      <span>{a.label}</span>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onAction && onAction('view', r)}
                  className="cursor-pointer"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  <span>Ver detalles</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default OperationCard;
